import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import * as R from "ramda";
import { useEffect, useState } from "react";

import { AnalysisGroup, AnalysisGroups } from "../../../types/account";

// Extract form state into a typed interface
type FormValues = {
  name: string;
  emailAddress: string;
  users: string;
  lenses: string;
};

// Extract reusable edit row component
type EditRowProps = {
  values: FormValues;
  onChange: (field: keyof FormValues, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  disabled: boolean;
};

// Pure validation functions
const validateEmail = (email: string): boolean => {
  if (!email) {
    return true;
  }
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const parseUsersFromText = (text: string): string[] =>
  R.pipe(
    R.split(","),
    R.map(R.trim),
    R.filter((email: string) => Boolean(email) && validateEmail(email))
  )(text || "");

const parseLensesFromText = (text: string): string[] =>
  R.pipe(
    R.split(","),
    R.map(R.trim)
  )(text || "");

interface EmailBccGroupsGridProps {
  emailBccGroups: AnalysisGroups | null;
  onChange: (groups: AnalysisGroups) => void;
  disabled?: boolean;
}

const EditRow = ({ values, onChange, onSave, onCancel, disabled }: EditRowProps) => {
  const isEmailValid = values.emailAddress === "" || validateEmail(values.emailAddress);
  const areLensesValid = values.lenses === "" || parseLensesFromText(values.lenses).length > 0;
  const isSaveDisabled = disabled || !values.name || !values.emailAddress || !isEmailValid || !areLensesValid;
  const areUsersValid = parseUsersFromText(values.users).length > 0;

  return (
    <TableRow sx={{ verticalAlign: "top" }}>
      <TableCell width="200">
        <TextField
          fullWidth
          value={values.name}
          onChange={(e) => onChange("name", e.target.value)}
          size="small"
          disabled={disabled}
          placeholder="My Group"
          helperText="Enter group name"
        />
      </TableCell>
      <TableCell width="300">
        <TextField
          fullWidth
          value={values.emailAddress}
          onChange={(e) => onChange("emailAddress", e.target.value)}
          size="small"
          disabled={disabled}
          error={!isEmailValid}
          placeholder="mygroup@example.com"
          helperText="Enter central inbox email address for this group"
          autoComplete="off"
          autoCapitalize="off"
        />
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          value={values.users}
          onChange={(e) => onChange("users", e.target.value)}
          size="small"
          disabled={disabled}
          error={!areUsersValid && values.users !== ""}
          placeholder="user1@example.com, user2@example.com"
          helperText="Enter email addresses separated by commas"
          autoComplete="off"
          autoCapitalize="off"
        />
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          value={values.lenses}
          onChange={(e) => onChange("lenses", e.target.value)}
          size="small"
          error={!areLensesValid && values.lenses !== ""}
          placeholder="lens-group"
          helperText="Enter custom lens group name. This will replace the default industry lens for this group of users."
        />
      </TableCell>
      <TableCell width="100">
        <IconButton
          onClick={onSave}
          disabled={isSaveDisabled}
          size="small"
          color="primary"
        >
          <SaveIcon />
        </IconButton>
        <IconButton
          onClick={onCancel}
          disabled={disabled}
          size="small"
        >
          <CancelIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export const EmailBccGroupsGrid = ({
  emailBccGroups,
  onChange,
  disabled = false
}: EmailBccGroupsGridProps) => {
  const [groups, setGroups] = useState<AnalysisGroup[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({
    name: "",
    emailAddress: "",
    users: "",
    lenses: ""
  });

  // Initialize groups from prop
  useEffect(() => {
    setGroups(emailBccGroups?.groups || []);
  }, [emailBccGroups]);

  const updateFormField = (field: keyof FormValues, value: string): void => {
    setFormValues(R.assoc(field, value, formValues));
  };

  const resetForm = (): void => {
    setFormValues({ name: "", emailAddress: "", users: "", lenses: "" });
    setEditIndex(null);
    setIsAddingNew(false);
  };

  const handleSave = (): void => {
    if (!isAddingNew && editIndex === null) {
      return;
    }
    if (!formValues.name || !formValues.emailAddress || !validateEmail(formValues.emailAddress)) {
      return;
    }

    const validUsers = parseUsersFromText(formValues.users);
    const validLenses = parseLensesFromText(formValues.lenses);
    const newGroup: AnalysisGroup = {
      name: formValues.name,
      emailAddress: formValues.emailAddress,
      users: validUsers,
      lenses: validLenses
    };

    const updatedGroups = isAddingNew
      ? R.append(newGroup, groups)
      : R.update(editIndex!, newGroup, groups);

    setGroups(updatedGroups);
    onChange({ groups: updatedGroups });
    resetForm();
  };

  const handleEditGroup = (index: number): void => {
    const group = groups[index];
    setEditIndex(index);
    setFormValues({
      name: group.name,
      emailAddress: group.emailAddress,
      users: (group.users || []).join(", "),
      lenses: (group.lenses || []).join(", ")
    });
  };

  const handleDeleteGroup = (index: number): void => {
    const updatedGroups = R.remove(index, 1, groups);
    setGroups(updatedGroups);
    onChange({ groups: updatedGroups });
  };

  const isEditingDisabled = editIndex !== null || isAddingNew;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Analysis Groups
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure custom lenses and central inbox email addresses for specific groups of users
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="200">Group Name</TableCell>
              <TableCell width="300">Central Inbox Email Address</TableCell>
              <TableCell>Email Addresses of Agents in this group (comma separated)</TableCell>
              <TableCell>Custom Lens Group</TableCell>
              <TableCell width="100">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group, index) =>
              editIndex === index ? (
                <EditRow
                  key={index}
                  values={formValues}
                  onChange={updateFormField}
                  onSave={handleSave}
                  onCancel={resetForm}
                  disabled={disabled}
                />
              ) : (
                <TableRow key={index}>
                  <TableCell width="200">{group.name}</TableCell>
                  <TableCell width="300">{group.emailAddress}</TableCell>
                  <TableCell>{(group.users || []).join(", ")}</TableCell>
                  <TableCell>{(group.lenses || []).join(", ")}</TableCell>
                  <TableCell width="100">
                    <IconButton
                      onClick={() => handleEditGroup(index)}
                      disabled={disabled || isEditingDisabled}
                      size="small"
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteGroup(index)}
                      disabled={disabled || isEditingDisabled}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            )}
            {isAddingNew && (
              <EditRow
                values={formValues}
                onChange={updateFormField}
                onSave={handleSave}
                onCancel={resetForm}
                disabled={disabled}
              />
            )}
            {groups.length === 0 && !isAddingNew && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No distribution groups configured
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setIsAddingNew(true)}
        disabled={disabled || isEditingDisabled}
        sx={{ mb: 2 }}
      >
        Add Group
      </Button>
    </Box>
  );
};
